import {Button, Icon, Notification, Text} from '@smart/react-components';
import type {FormEventHandler} from 'react';
import {useEffect} from 'react';
import {useRef} from 'react';
import React, {useCallback, useMemo, useState} from 'react';
import {object} from 'yup';
import FormStyles from '../../common/form-styles.module.scss';
import {scrollToRef} from '../../common/helpers';
import type {ViewsFetchingToPostForm} from '../../common/types';
import {AppModelContext} from '../../components/app';
import Intercept from '../../components/easy-form/Intercept';
import Fields from '../../components/easy-form/fields';
import type {
  ErrorType,
  JSONObject,
  StateType,
  WrapperHandler,
  preValidationTransform,
} from '../../components/easy-form/types';
import MultiCheckbox from '../../components/multi-checkbox';
import MultiRadio from '../../components/multi-radio';
import {useAPI} from '../../hooks/use-api';
import {useEntry} from '../../hooks/use-entry';
import {useTracking} from '../../hooks/use-tracking';
import type {ITrackContentPrefs} from '../../hooks/use-tracking/types';
import {useViews} from '../../hooks/use-views';
import {useSchemas} from '../../hooks/user-schemas';
import type {Success} from '../success';

const _iDriveOptions = [
  `ownCar`,
  `companyCar`,
  `leasingCar`,
  `servicesCar`,
] as const;
const _purposeOptions = [`forShopping`, `forTravel`, `forNothing`] as const;
const _iHaveOptions = [`evExperience`, `wallbox`] as const;
const _carInterestsOptions = [`leasing`, `new`, `cash`, `used`] as const;
const _interestsOptions = [
  `serviceAdditional`,
  `carNews`,
  `educational`,
  `customerStories`,
  `mobilityFuture`,
] as const;

export type ContentPrefsPurposeOptionType = (typeof _purposeOptions)[number];
export type ContentPrefsIHaveOptionsType = (typeof _iHaveOptions)[number];
export type ContentPrefsCarInterestsOptionsType =
  (typeof _carInterestsOptions)[number];
export type ContentPrefsInterestsOptionsType =
  (typeof _interestsOptions)[number];

interface iDriveType extends JSONObject {
  transport: (typeof _iDriveOptions)[number];
}

interface IDriveLeasingCar extends iDriveType {
  leasingCar: {
    brand: string;
    model: string;
    ev: boolean;
  };
}

interface IDriveOwnCar extends iDriveType {
  ownCar: {
    brand: string;
    model: string;
    ev: boolean;
  };
}

interface IDriveCompanyCar extends iDriveType {
  companyCar: {
    profession: string;
    company: string;
  };
}

interface IDriveServicesCar extends iDriveType {
  servicesCar: {
    Other: string;
    sharing: Array<ContentPrefsPurposeOptionType>;
  };
}

export interface IContentPrefsState extends StateType {
  iDrive:
    | IDriveLeasingCar
    | IDriveOwnCar
    | IDriveCompanyCar
    | IDriveServicesCar;
  carInterests?: Array<ContentPrefsCarInterestsOptionsType>;
  iHave?: Array<ContentPrefsIHaveOptionsType>;
  interests: Array<ContentPrefsInterestsOptionsType>;
}

const viewName: ViewsFetchingToPostForm = `content-prefs`;

// eslint-disable-next-line complexity
export default function ContentPrefs(): JSX.Element {
  const [submitAttempt, setSubmitAttempt] = useState(false);
  const [hasErrors, setHasErrors] = useState(true);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState<string>();
  const apiFetch = useAPI(viewName);
  const [entry] = useEntry();
  const [iDriveChosen, setIDriveChosen] = useState<
    (typeof _iDriveOptions)[number] | ``
  >(``);
  const formData = useRef({
    state: undefined as IContentPrefsState | undefined,
    errors: undefined as ErrorType | undefined,
  });
  const {setView} = useViews();
  const [trackStep, successTrack] = useTracking<ITrackContentPrefs>(
    `newsletter_rms`,
    `content-prefs`,
  );
  const cmsTranslations =
    React.useContext(AppModelContext).cms.formsFeatureAppContentPreferencesUi ||
    {};
  const cmsTranslationsErrors =
    React.useContext(AppModelContext).cms.formsFeatureAppValidation_ErrorsUi ||
    {};
  const apiTranslations =
    React.useContext(AppModelContext).cms.apiErrorMessageKeys || {};
  const cmsTranslationsForTransportOptions = [
    cmsTranslations.contentPrefs_iDrive_transport_ownCar,
    cmsTranslations.contentPrefs_iDrive_transport_companyCar,
    cmsTranslations.contentPrefs_iDrive_transport_leasingCar,
    cmsTranslations.contentPrefs_iDrive_transport_servicesCar,
  ];
  const cmsTranslationsForTransportOptionsMap = {
    ownCar: cmsTranslations.contentPrefs_iDrive_transport_ownCar,
    companyCar: cmsTranslations.contentPrefs_iDrive_transport_companyCar,
    leasingCar: cmsTranslations.contentPrefs_iDrive_transport_leasingCar,
    servicesCar: cmsTranslations.contentPrefs_iDrive_transport_servicesCar,
  };
  const cmsTranslationsForPurposeOptions = [
    cmsTranslations.contentPrefs_iDrive_servicesCar_forShopping,
    cmsTranslations.contentPrefs_iDrive_servicesCar_forTravel,
    cmsTranslations.contentPrefs_iDrive_servicesCar_forNothing,
  ];
  const cmsTranslationsForIHaveOptions = [
    cmsTranslations.contentPrefs_iHave_evExperience,
    cmsTranslations.contentPrefs_iHave_wallbox,
  ];
  const cmsTranslationsForCarInterestsOptions = [
    cmsTranslations.contentPrefs_carInterests_leasing,
    cmsTranslations.contentPrefs_carInterests_new,
    cmsTranslations.contentPrefs_carInterests_cash,
    cmsTranslations.contentPrefs_carInterests_used,
  ];
  const cmsTranslationsForInterestsOptions = [
    cmsTranslations.contentPrefs_interests_serviceAdditional,
    cmsTranslations.contentPrefs_interests_carNews,
    cmsTranslations.contentPrefs_interests_educational,
    cmsTranslations.contentPrefs_interests_customerStories,
    cmsTranslations.contentPrefs_interests_mobilityFuture,
  ];

  const formWrapperRef = useRef<HTMLDivElement>(null);

  const iDriveOptions = _iDriveOptions as unknown as string[];
  const purposeOptions = _purposeOptions as unknown as string[];
  const iHaveOptions = _iHaveOptions as unknown as string[];
  const carInterestsOptions = _carInterestsOptions as unknown as string[];
  const interestsOptions = _interestsOptions as unknown as string[];

  useEffect(() => {
    if (entry === viewName) {
      trackStep(1, `form_start`);
    }
  }, []);

  const onSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    const data = formData.current;
    // UPDATE WHEN ERROR CODES ARE AVAILABLE FROM API
    // const cfmError = cmsTranslationsErrors.validations_serverError?.toString();
    event.preventDefault();
    if (data.errors || !data.state) {
      setSubmitAttempt(true);
      scrollToRef(formWrapperRef);
      return;
    }

    setLoading(true);
    setServerError(undefined);

    try {
      trackStep(3, `form_engagement`);
      await apiFetch(data.state);
      successTrack(5, `success-content-prefs`);
      setView<typeof Success>(`success`, {
        successGreeting: cmsTranslations.contentPrefs_successPage_greeting,
        successTitle: cmsTranslations.contentPrefs_successPage_title,
      });
    } catch (error) {
      setServerError(error as string);
      setLoading(false);
      scrollToRef(formWrapperRef);
    }
  };

  const situationButtonHandler = useCallback(() => {
    const iDrive = (formData.current.state as any).iDrive?.transport;
    if (iDrive) {
      setSubmitAttempt(false);
      setIDriveChosen((prevSituation) => {
        return iDrive !== prevSituation ? iDrive : ``;
      });
    } else {
      setSubmitAttempt(true);
      scrollToRef(formWrapperRef);
    }
  }, [formData]);

  const changeHandler: WrapperHandler = (state, errors) => {
    formData.current = {
      state: state as IContentPrefsState,
      errors,
    };
    setHasErrors(!!errors);
  };

  const {singleLine} = useSchemas();

  const schemas = useMemo(() => {
    return {
      ownCarOrLeasingCar: {
        brand: singleLine(true),
        model: singleLine(true),
      },
      companyCar: {
        profession: singleLine(),
        company: singleLine(),
      },
      servicesCar: object().test({
        name: `dependent`,
        message: cmsTranslationsErrors.validations_array_min?.toString,
        test: (value: any) => {
          const {sharing, Other}: {sharing: string[]; Other: string} =
            value as any;
          return !(!sharing.length && !Other.length);
        },
      }),
    };
  }, [singleLine]);

  const transformService: preValidationTransform = (merge) => {
    return {
      sharing: Object.entries(merge)
        .filter((pair) => !!pair[1] && pair[0] !== `other`)
        .map((pair) => pair[0]),
      Other: merge.other,
    };
  };
  const setValueFromAEM = () => {
    let apiErrorMessage = ``;
    let translatedString;

    const getStringFromAEM = apiTranslations.errorMessageKeys?.filter((e) => {
      return e?.includes(serverError as any);
    }, serverError);

    if (!getStringFromAEM) {
      apiErrorMessage = apiTranslations.generalFallbackErrorMessage as string;
    } else {
      translatedString = getStringFromAEM && getStringFromAEM[0]?.split(`:`);
      apiErrorMessage = translatedString && (translatedString[1] as any);
    }
    return apiErrorMessage;
  };

  const nextButtonDisabled =
    (submitAttempt &&
      hasErrors &&
      (!iDriveChosen || iDriveChosen === cmsTranslations.contentPrefs_next)) ||
    loading;

  return (
    <div className={FormStyles.wrapper} ref={formWrapperRef}>
      <Fields.Wrapper
        translationPrefix={viewName}
        onUpdate={changeHandler}
        showErrors={submitAttempt}
        disabled={loading}
      >
        <Icon
          className={FormStyles.centerIcon}
          icon="successful-checkmark"
          mode={300}
        />

        <Text className={FormStyles.heading} variant="hl-300" as="p">
          {cmsTranslations.contentPrefs_callToAction}
        </Text>

        <Text className={FormStyles.upperText} variant="p-100" as="p">
          {cmsTranslations.contentPrefs_title}
        </Text>

        {serverError && (
          <Notification
            signal="error"
            className={FormStyles.serverError}
            onOpenChange={(open) => {
              if (!open) {
                setServerError(undefined);
              }
            }}
          >
            <Notification.Text>{setValueFromAEM()}</Notification.Text>
          </Notification>
        )}

        <form onSubmit={onSubmit}>
          <Intercept
            tag="iDrive"
            set
            required
            label={cmsTranslations.contentPrefs_iDrive}
          >
            <MultiRadio
              options={iDriveChosen ? [iDriveChosen] : iDriveOptions}
              hideLegend
              required
              tag="transport"
              optionsLabel={
                iDriveChosen
                  ? [cmsTranslationsForTransportOptionsMap[iDriveChosen]]
                  : cmsTranslationsForTransportOptions
              }
            />

            {iDriveChosen === `ownCar` ? (
              <Intercept tag="ownCar">
                <Fields.Text
                  tag="brand"
                  required
                  schema={schemas.ownCarOrLeasingCar.brand}
                  label={cmsTranslations.contentPrefs_iDrive_ownCar_brand}
                />
                <Fields.Text
                  tag="model"
                  required
                  schema={schemas.ownCarOrLeasingCar.model}
                  label={cmsTranslations.contentPrefs_iDrive_ownCar_model}
                />
                <Fields.Checkbox
                  name="choice"
                  tag="ev"
                  label={cmsTranslations.contentPrefs_iDrive_ownCar_ev}
                />
              </Intercept>
            ) : iDriveChosen === `leasingCar` ? (
              <Intercept tag="leasingCar">
                <Fields.Text
                  tag="brand"
                  schema={schemas.ownCarOrLeasingCar.brand}
                  label={cmsTranslations.contentPrefs_iDrive_leasingCar_brand}
                />
                <Fields.Text
                  tag="model"
                  schema={schemas.ownCarOrLeasingCar.model}
                  label={cmsTranslations.contentPrefs_iDrive_leasingCar_model}
                />
                <Fields.Checkbox
                  name="choice"
                  tag="ev"
                  label={cmsTranslations.contentPrefs_iDrive_leasingCar_ev}
                />
              </Intercept>
            ) : iDriveChosen === `companyCar` ? (
              <Intercept tag="companyCar">
                <Fields.Text
                  tag="profession"
                  schema={schemas.companyCar.profession}
                  label={
                    cmsTranslations.contentPrefs_iDrive_companyCar_profession
                  }
                />
                <Fields.Text
                  tag="company"
                  schema={schemas.companyCar.company}
                  label={cmsTranslations.contentPrefs_iDrive_companyCar_company}
                />
              </Intercept>
            ) : iDriveChosen === `servicesCar` ? (
              <Intercept
                tag="servicesCar"
                set
                hideLegend
                schema={schemas.servicesCar}
                outputTransform={transformService}
                setProps={{className: FormStyles.subField}}
                label={
                  cmsTranslations.contentPrefs_iDrive_transport_servicesCar
                }
              >
                {purposeOptions.map((opt, index) => (
                  <Fields.Checkbox
                    key={opt}
                    tag={opt}
                    label={cmsTranslationsForPurposeOptions[index]}
                  />
                ))}

                <Fields.Text
                  tag="other"
                  label={cmsTranslations.contentPrefs_iDrive_servicesCar_other}
                />
              </Intercept>
            ) : null}
          </Intercept>

          {iDriveChosen && (
            <>
              {(iDriveChosen === `ownCar` || iDriveChosen === `leasingCar`) && (
                <>
                  <MultiCheckbox
                    label={cmsTranslations.contentPrefs_iHave}
                    options={iHaveOptions}
                    tag="iHave"
                    labels={cmsTranslationsForIHaveOptions as string[]}
                  />

                  <MultiRadio
                    label={cmsTranslations.contentPrefs_carInterests}
                    options={carInterestsOptions}
                    tag="carInterests"
                    required
                    optionsLabel={cmsTranslationsForCarInterestsOptions}
                  />
                </>
              )}

              <MultiCheckbox
                label={cmsTranslations.contentPrefs_interests}
                options={interestsOptions}
                tag="interests"
                min={1}
                labels={cmsTranslationsForInterestsOptions as string[]}
              />

              <Button
                type="submit"
                variant={`primary`}
                className={FormStyles.button}
                disabled={(submitAttempt && hasErrors) || loading}
              >
                {cmsTranslations.contentPrefs_submit || `Submit`}
              </Button>
            </>
          )}

          <Button
            type="button"
            variant={iDriveChosen ? `secondary` : `primary`}
            className={FormStyles.button}
            onClick={situationButtonHandler}
            disabled={nextButtonDisabled}
          >
            {iDriveChosen
              ? cmsTranslations.contentPrefs_back || `Back`
              : cmsTranslations.contentPrefs_next || `Next`}
          </Button>
        </form>
      </Fields.Wrapper>
    </div>
  );
}
